import { Search, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { env } from 'src/config/env'
import { paths } from 'src/config/paths'
import { Logo } from 'src/shared/components/icons/logos/logo'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useDebounce } from 'src/shared/hooks/use-debounce'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { useSignOut } from '../../auth/api'
import { useAuthState } from '../../auth/api/use-auth-state'
import { DirectionArrow } from '../../users/assets/direction-arrow'
import { LogoutIcon } from '../../users/components/marketplace/icons/logout'
import AuthModal from '../../users/components/marketplace/modals/auth'
import { ProfilePicIcon } from '../assets/pfp'
import { HelpCenterIcon } from '../icons/help-center'

import SearchContent from './search/search-content'

const CustomButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => {
  return (
    <button
      className="flex h-[80px] w-full flex-row items-center justify-between gap-3 rounded-[25px] border-[0.5px] border-border bg-accent p-5 md:h-[100px] lg:p-8"
      style={{
        boxShadow: '0px 2px 12px 1px rgba(27, 48, 41, 0.05)',
      }}
      onClick={onClick}
    >
      <p className="text-left text-lg font-bold text-primary lg:text-[20px]">{children}</p>
      <Button size="icon" className="size-[40px] border border-border bg-accent text-primary lg:size-[50px]">
        <DirectionArrow className="size-5" />
      </Button>
    </button>
  )
}

const InstantMatchButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      className="flex h-[80px] w-full flex-row items-center justify-between gap-3 rounded-[25px] border-[0.5px] border-border bg-primary p-5 md:h-[100px] lg:p-8"
      style={{
        boxShadow: '0px 2px 12px 1px rgba(27, 48, 41, 0.05)',
      }}
      onClick={onClick}
    >
      <p className="text-left text-lg font-bold text-white lg:text-[20px]">Instant Match Me!</p>
      <Button size="icon" className="size-[40px] border border-border bg-secondaryInverse text-primary lg:size-[50px]">
        <DirectionArrow className="size-5" />
      </Button>
    </button>
  )
}

type SimpleSidebarProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const SimpleSidebar = ({ isOpen, setIsOpen }: SimpleSidebarProps) => {
  const isMobile = useIsMobile()
  const { isConsumerSignedIn, isSignedIn } = useAuthState()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [authModalFlow, setAuthModalFlow] = useState<'signup' | 'login'>('login')
  const signOut = useSignOut({ isConsumer: true })
  const [showSearchResults, setShowSearchResults] = useState(false)

  const {
    value: searchText,
    debouncedValue: debouncedQuery,
    setValue: setSearchText,
  } = useDebounce<string>('', {
    delay: 300,
  })

  const onInstantMatch = () => {
    navigate(paths.marketplace.instantMatch.getHref())
  }

  const onFindAgent = () => {
    navigate(paths.marketplace.instantMatch.profession.getHref('real_estate_agent'))
  }

  const onGetMortgage = () => {
    navigate(paths.marketplace.instantMatch.profession.getHref('mortgage_loan_officer', 'mortgage'))
  }

  const onHelpCenterClick = () => {
    window.open(env.LINK_TO_HELP_CENTER, '_blank')
  }

  const handleLogout = async () => {
    await signOut.mutateAsync()
    navigate(paths.marketplace.getHref())
  }

  const handleClientLoginClick = (signup = false) => {
    setIsOpen(false)
    setShowModal(true)
    setAuthModalFlow(signup ? 'signup' : 'login')
  }

  const close = () => {
    setIsOpen(false)
    setShowSearchResults(false)
    setShowModal(false)
    setSearchText('')
  }

  if (!isMobile) return null

  return (
    <>
      <div
        className={cn(
          'duration-[400ms] fixed inset-0 z-[51] bg-black/30 transition-opacity',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={close}
      />
      <aside
        className={cn(
          'duration-[400ms] fixed left-0 top-0 z-[100] flex h-screen w-screen flex-col gap-[24px] bg-white transition-transform sm:w-[400px]',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ boxShadow: '2px 0 16px 0 rgba(0,0,0,0.10)' }}
      >
        <div className="relative flex h-[60px] flex-row items-center justify-between px-5 py-6 pb-2">
          <div className="flex flex-1 items-center justify-center">
            <Logo className="h-[25px] w-[129px]" />
          </div>
          <button
            onClick={close}
            className="absolute left-5 top-5 flex size-[35px] items-center justify-center rounded-full bg-white"
            style={{
              boxShadow: '0 0 7px 1px rgba(27,48,41,0.1)',
            }}
          >
            <X className="size-[25px] text-muted" />
          </button>
        </div>
        <div className="flex flex-col gap-5 overflow-y-auto px-5 pt-2">
          <div className="relative flex h-[55px] flex-row items-center gap-2.5 rounded-full border-[0.5px] border-border px-5 py-3">
            <Search className="size-5 text-muted" />
            <input
              type="text"
              placeholder="Enter professional's name"
              className="w-full pr-8 text-base font-medium text-muted placeholder:text-muted focus:outline-none"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value)
                setShowSearchResults(e.target.value.length > 0)
              }}
              onFocus={() => setShowSearchResults(debouncedQuery.length > 0)}
            />
            {searchText.length > 0 && (
              <button
                className="absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-border"
                onClick={() => setSearchText('')}
              >
                <X className="size-4 text-primary" />
              </button>
            )}
          </div>
          {showSearchResults ? (
            <div className="h-[calc(100vh-60px-24px-120px)]">
              <SearchContent query={debouncedQuery} />
            </div>
          ) : (
            <>
              <CustomButton onClick={onFindAgent}>Find an agent</CustomButton>
              <CustomButton onClick={onGetMortgage}>Get a mortgage</CustomButton>
              <InstantMatchButton onClick={onInstantMatch} />
            </>
          )}
        </div>
        {!showSearchResults && (
          <div className="mt-auto flex flex-col gap-3 px-5 py-6">
            <div className="mb-10 flex w-full flex-row gap-3 border-t-[0.5px] border-border pt-5">
              <button
                onClick={onHelpCenterClick}
                className="flex h-[50px] flex-1 items-center justify-center gap-3 bg-white text-base font-semibold text-primary"
              >
                <HelpCenterIcon className="size-5" />
                <p className="text-primary">Help center</p>
              </button>
              <button
                onClick={() => (isConsumerSignedIn || isSignedIn ? handleLogout() : handleClientLoginClick(true))}
                className="flex h-[50px] flex-1 items-center justify-center gap-3 text-base font-semibold text-primary"
              >
                {isConsumerSignedIn || isSignedIn ? (
                  <LogoutIcon className="size-5" />
                ) : (
                  <ProfilePicIcon className="size-5" />
                )}
                <p className="text-primary">{isConsumerSignedIn || isSignedIn ? 'Log out' : 'Sign in'}</p>
              </button>
            </div>
          </div>
        )}
      </aside>
      <AuthModal flow={authModalFlow} open={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default SimpleSidebar
